import React, { useCallback } from 'react';
import { Group, Rect, Text, Circle } from 'react-konva';

/**
 * ContainerHoverMenu - Native hover menu for container actions
 * Replaces react-contexify with pure Konva components
 */
const ContainerHoverMenu = ({
  x,
  y,
  visible,
  onAction,
  onHoverStart,
  onHoverEnd,
  containerLocked = false,
  containerName = 'Container',
  stageScale = 1 // Add stageScale prop for zoom-independent sizing
}) => {
  // All hooks must be called before any early returns
  const handleItemClick = useCallback((action) => {
    onAction?.(action);
  }, [onAction]);
  
  // --- תיקון: מערך refs אחד לכל הכפתורים ---
  const itemRefs = React.useRef([]);
  
  // Early return after all hooks
  if (!visible) return null;

  // Calculate scale factor to keep UI consistent size regardless of zoom
  // When zoomed in (stageScale > 1), make elements smaller to compensate
  // When zoomed out (stageScale < 1), make elements larger to compensate
  const uiScale = 1 / stageScale;
  
  const menuItems = [
    {
      icon: '\uf2ed', // fa-trash (Font Awesome Unicode)
      action: 'delete',
      label: 'Delete',
      color: '#ffffff',
      hoverColor: 'var(--error-color, #dc3545)'
    },
    {
      icon: containerLocked ? '\uf09c' : '\uf023', // fa-unlock / fa-lock
      action: 'toggleLock',
      label: containerLocked ? 'Unlock' : 'Lock',
      color: '#ffffff',
      hoverColor: 'var(--secondary-color, #d9963b)'
    },
    {
      icon: '\uf062', // fa-arrow-up
      action: 'toFront',
      label: 'Up',
      color: '#ffffff',
      hoverColor: 'var(--primary-color, #a02c2c)'
    },
    {
      icon: '\uf063', // fa-arrow-down
      action: 'toBack',
      label: 'Down',
      color: '#ffffff',
      hoverColor: 'var(--primary-color, #a02c2c)'
    },
    {
      icon: '\uf0c5', // fa-copy
      action: 'duplicate',
      label: 'Copy',
      color: '#ffffff',
      hoverColor: 'var(--accent-color, #f9d276)'
    }
  ];
  
  // Scale-adjusted measurements
  const itemPaddingX = 6 * uiScale;
  const itemGap = 4 * uiScale;
  const iconSize = 11 * uiScale;
  const textSize = 13 * uiScale;
  const buttonHeight = 24 * uiScale;
  const menuHeight = 40 * uiScale;
  
  const estimatedItemWidths = menuItems.map(item => {
    const iconWidth = iconSize;
    const iconTextGap = 4 * uiScale;
    const avgCharWidth = 9.5 * uiScale;
    const textWidth = item.label.length * avgCharWidth;
    const totalPadding = itemPaddingX * 2;
    const arrowAdjustment = (item.action === 'toFront' || item.action === 'toBack') ? -3 * uiScale : 0;
    
    return iconWidth + iconTextGap + textWidth + totalPadding + arrowAdjustment + (8 * uiScale);
  });
  const totalItemsWidth = estimatedItemWidths.reduce((sum, width) => sum + width, 0);
  const totalGapsWidth = (menuItems.length - 1) * itemGap;
  const menuPadding = 16 * uiScale;
  const menuWidth = totalItemsWidth + totalGapsWidth + menuPadding;

  return (
    <Group 
      x={x - menuWidth / 2} 
      y={y}
      onMouseEnter={() => {
        // Keep menu visible when hovering over it
        onHoverStart?.();
      }}
      onMouseLeave={() => {
        // Allow menu to hide when leaving it
        onHoverEnd?.();
      }}
    >
      
      {/* Menu background */}
      <Rect
        x={0}
        y={0}
        width={menuWidth}
        height={menuHeight}
        fill="rgba(0, 0, 0, 0.95)"
        stroke="#666666"
        strokeWidth={1 * uiScale}
        cornerRadius={8 * uiScale}
        shadowColor="rgba(0, 0, 0, 0.8)"
        shadowBlur={15 * uiScale}
        shadowOffset={{ x: 3 * uiScale, y: 3 * uiScale }}
        shadowOpacity={0.9}
        listening={true}
      />
      
      {/* Container name label */}
      <Text
        x={8 * uiScale}
        y={6 * uiScale}
        text={containerName}
        fontSize={10 * uiScale}
        fontFamily="var(--font-secondary, 'EB Garamond', serif)"
        fill="#ffffff"
        width={menuWidth - (16 * uiScale)}
        wrap="none"
        ellipsis={false}
        fontStyle="italic"
        align="left"
      />
      
      {/* Menu items - tool-button style */}
      {menuItems.map((item, index) => {
        const itemWidth = estimatedItemWidths[index];
        const itemX = index === 0 ? (8 * uiScale) : estimatedItemWidths.slice(0, index).reduce((sum, w) => sum + w, 0) + (index * itemGap) + (8 * uiScale);
        
        const handleMouseEnter = () => {
          const ref = itemRefs.current[index];
          if (ref) {
            ref.fill('#f9d276');
            ref.stroke('#f9d276');
            ref.getLayer().batchDraw();
          }
          const iconRef = itemRefs.current[`icon-${index}`];
          const textRef = itemRefs.current[`text-${index}`];
          if (iconRef) {
            iconRef.fill('#141414');
            iconRef.getLayer().batchDraw();
          }
          if (textRef) {
            textRef.fill('#141414');
            textRef.getLayer().batchDraw();
          }
        };
        
        const handleMouseLeave = () => {
          const ref = itemRefs.current[index];
          if (ref) {
            ref.fill('#333333');
            ref.stroke('#555555');
            ref.getLayer().batchDraw();
          }
          const iconRef = itemRefs.current[`icon-${index}`];
          const textRef = itemRefs.current[`text-${index}`];
          if (iconRef) {
            iconRef.fill('#ffffff');
            iconRef.getLayer().batchDraw();
          }
          if (textRef) {
            textRef.fill('#ffffff');
            textRef.getLayer().batchDraw();
          }
        };
        
        const handleClick = () => handleItemClick(item.action);

        return (
          <Group key={item.action} x={itemX} y={18 * uiScale}>
            {/* Tool-button style background */}
            <Rect
              ref={el => itemRefs.current[index] = el}
              x={0}
              y={0}
              width={itemWidth}
              height={buttonHeight}
              fill="#333333"
              stroke="#555555"
              strokeWidth={1 * uiScale}
              cornerRadius={2 * uiScale}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
            />
            
            {/* Item icon */}
            <Text
              ref={el => itemRefs.current[`icon-${index}`] = el}
              x={itemPaddingX}
              y={buttonHeight / 2}
              text={item.icon}
              fontSize={iconSize}
              fontFamily="'Font Awesome 6 Free', 'Font Awesome 5 Free', 'FontAwesome', Arial"
              fontStyle="900"
              fill="#ffffff"
              offsetY={iconSize / 2}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
            />
            
            {/* Item label */}
            <Text
              ref={el => itemRefs.current[`text-${index}`] = el}
              x={itemPaddingX + iconSize + (4 * uiScale) + (item.action === 'toFront' || item.action === 'toBack' ? -3 * uiScale : 0)}
              y={buttonHeight / 2 + (1 * uiScale)}
              text={item.label}
              fontSize={textSize}
              fontFamily="'Cinzel', 'Eczar', 'Alegreya', serif"
              fontStyle="700"
              fill="#ffffff"
              lineHeight={1}
              offsetY={textSize / 2}
              width={itemWidth - (itemPaddingX + iconSize + (4 * uiScale) + (item.action === 'toFront' || item.action === 'toBack' ? -3 * uiScale : 0)) - itemPaddingX}
              wrap="none"
              ellipsis={false}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
            />
          </Group>
        );
      })}
    </Group>
  );
};

/**
 * ContainerSelectionIndicator - Visual indicator for selected container
 */
export const ContainerSelectionIndicator = ({
  x,
  y,
  width,
  height,
  visible = true,
  color = '#0066ff'
}) => {
  if (!visible) return null;
  
  return (
    <Group x={x} y={y}>
      {/* Corner indicators */}
      <Circle
        x={0}
        y={0}
        radius={4}
        fill={color}
        stroke="white"
        strokeWidth={1}
      />
      <Circle
        x={width}
        y={0}
        radius={4}
        fill={color}
        stroke="white"
        strokeWidth={1}
      />
      <Circle
        x={0}
        y={height}
        radius={4}
        fill={color}
        stroke="white"
        strokeWidth={1}
      />
      <Circle
        x={width}
        y={height}
        radius={4}
        fill={color}
        stroke="white"
        strokeWidth={1}
      />
      
      {/* Border */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        stroke={color}
        strokeWidth={2}
        dash={[8, 4]}
        listening={false}
      />
    </Group>
  );
};

/**
 * ContainerLoadingIndicator - Shows loading state for containers
 */
export const ContainerLoadingIndicator = ({
  x,
  y,
  width,
  height,
  progress = 0
}) => {
  return (
    <Group x={x} y={y}>
      {/* Background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(240, 240, 240, 0.8)"
        stroke="#cccccc"
        strokeWidth={1}
        cornerRadius={4}
      />
      
      {/* Progress bar background */}
      <Rect
        x={width / 4}
        y={height / 2 - 4}
        width={width / 2}
        height={8}
        fill="#eeeeee"
        cornerRadius={4}
      />
      
      {/* Progress bar fill */}
      <Rect
        x={width / 4}
        y={height / 2 - 4}
        width={(width / 2) * (progress / 100)}
        height={8}
        fill="#4CAF50"
        cornerRadius={4}
      />
      
      {/* Loading text */}
      <Text
        x={0}
        y={height / 2 + 12}
        text={`Loading... ${Math.round(progress)}%`}
        fontSize={12}
        fontFamily="Arial"
        fill="#666666"
        width={width}
        align="center"
      />
    </Group>
  );
};

/**
 * ContainerErrorIndicator - Shows error state for containers
 */
export const ContainerErrorIndicator = ({
  x,
  y,
  width,
  height,
  errorMessage = 'Failed to load image'
}) => {
  return (
    <Group x={x} y={y}>
      {/* Background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(255, 240, 240, 0.8)"
        stroke="#ff6666"
        strokeWidth={1}
        cornerRadius={4}
      />
      
      {/* Error icon */}
      <Text
        x={width / 2 - 12}
        y={height / 2 - 20}
        text="⚠️"
        fontSize={24}
        fontFamily="Arial"
      />
      
      {/* Error text */}
      <Text
        x={8}
        y={height / 2 + 8}
        text={errorMessage}
        fontSize={10}
        fontFamily="Arial"
        fill="#cc0000"
        width={width - 16}
        align="center"
        wrap="word"
      />
    </Group>
  );
};

export default ContainerHoverMenu; 